import { useState } from "react";
import Button, { ButtonProps } from "../Button";
import FormDocumentUploadDropdown from "../FormDocumentUpload/FormDocumentUploadDropdown";
import CompanyDepartmentSelector, {
    useCompanyDepartmentSelector,
} from "./CompanyDepartmentSelector";
import DownloadReportButton from "./DownloadReportButton";
import { Minus, Plus } from "phosphor-react";
import clsx from "clsx";
import useAuth from "../../auth";
import { isS2dUser } from "../../auth/userAccessHelpers";

export type TailoredButton = {
    title: string;
    onClick: () => void;
    icon?: ButtonProps["icon"];
    variant?: ButtonProps["variant"];
};

type HeaderOptionsProps = {
    // Client/department:
    employmentFilters?: ReturnType<typeof useCompanyDepartmentSelector>;
    // Reports:
    handleDownloadPdfReport?: () => void;
    handleDownloadCsvReport?: () => void;
    handleDownloadXlsReport?: () => void;
    isReportDownloading?: boolean;
    isReportDisabled?: boolean;
    // Import:
    refetch?: () => void;
    // Tailored button:
    tailoredButtons?: TailoredButton[];
    // Date picker (not yet implemented anywhere)
};

const HeaderOptions: React.FC<HeaderOptionsProps> = ({
    employmentFilters,
    handleDownloadPdfReport,
    handleDownloadCsvReport,
    handleDownloadXlsReport,
    isReportDownloading,
    isReportDisabled,
    refetch,
    tailoredButtons,
}) => {
    const { decodedToken } = useAuth();
    const [isHidden, setIsHidden] = useState(true);
    const showFiltersText = isHidden
        ? "Show filters and actions"
        : "Hide filters and actions";
    const showFiltersIcon = isHidden ? Plus : Minus;

    const showCompanyDepartmentSelector = !!employmentFilters;
    const showDownloadReports = !!(
        handleDownloadCsvReport && handleDownloadPdfReport
    );
    const showImportCSV = !!refetch;
    const showTailoredButton = !!tailoredButtons && tailoredButtons.length;

    const showReset: boolean = employmentFilters?.clientHasNoChildren
        ? !!employmentFilters?.selectedDepartment
        : !!employmentFilters?.selectedCompany;

    return (
        <div className="flex flex-wrap items-start justify-between gap-4">
            <Button
                variant="secondary"
                onClick={() => setIsHidden(!isHidden)}
                className="min-w-fit grow justify-center md:hidden md:flex-none"
                icon={showFiltersIcon}
                iconEnd
            >
                {showFiltersText}
            </Button>
            <div
                className={clsx(
                    "flex w-full flex-col gap-4 md:w-fit md:flex-row",
                    isHidden && "hidden md:flex"
                )}
            >
                {showCompanyDepartmentSelector && (
                    <>
                        <CompanyDepartmentSelector
                            currentDepartment={
                                employmentFilters?.selectedDepartment ?? null
                            }
                            handleDepartmentChange={
                                employmentFilters?.handleDepartmentChange
                            }
                            selectedCompany={
                                employmentFilters?.selectedCompany ?? null
                            }
                            handleCompanyChange={
                                employmentFilters?.handleCompanyChange
                            }
                        />
                        {showReset && (
                            <Button
                                variant="tertiary"
                                onClick={
                                    employmentFilters?.handleResetSelectedCompanyDepartment
                                }
                                className="min-w-fit grow justify-center md:flex-none"
                            >
                                Reset
                            </Button>
                        )}
                    </>
                )}
            </div>
            <div
                className={clsx(
                    "flex w-full flex-col gap-4 md:w-fit md:flex-row",
                    isHidden && "hidden md:flex"
                )}
            >
                {showDownloadReports && (
                    <DownloadReportButton
                        handleDownloadCsvReport={handleDownloadCsvReport}
                        handleDownloadPdfReport={handleDownloadPdfReport}
                        handleDownloadXlsReport={handleDownloadXlsReport}
                        isReportDownloading={isReportDownloading ?? false}
                        isDisabled={isReportDisabled}
                    />
                )}

                {showImportCSV && isS2dUser(decodedToken?.role) && (
                    <FormDocumentUploadDropdown refetchDrivers={refetch} />
                )}

                {showTailoredButton &&
                    tailoredButtons.map((button) => (
                        <Button
                            icon={button.icon ?? undefined}
                            className="min-w-fit grow justify-center md:flex-none"
                            onClick={button.onClick}
                            variant={button.variant ?? "primary"}
                            key={button.title}
                        >
                            {button.title}
                        </Button>
                    ))}
            </div>
        </div>
    );
};

export default HeaderOptions;
